import * as THREE from 'three';
import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise.js';
import { RNG } from './rng';
import { blocks, resources } from './blocks';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/config';

const geometry = new THREE.BoxGeometry();

export class WorldChunk extends THREE.Group {
  idMap = null;
  instanceIdMap = null;

  constructor(size, params, dataStore) {
    super();
    this.loaded = false;
    this.size = size;
    this.params = params;
    this.size = size;
    this.params = params;
    this.dataStore = dataStore;
    this.disposed = false;
    this.onLoaded = null;
  }

  generate() {
    if (this.disposed) return;
    const rng = new RNG(this.params.seed);
    this.initializeTerrain();
    this.generateTerrain(rng);
    this.generateFlyingIslands(rng);
    this.generateClouds(rng);
    this.generateMeshes();
    this.loaded = true;
    this.onLoaded?.();
  }
  generateDuringUpdate() {
    if (this.disposed) return;
    const rng = new RNG(this.params.seed);
    this.initializeTerrain();
    this.generateTerrain(rng);
    this.generateFlyingIslands(rng);
    this.generateClouds(rng);
    this.generateMeshes();
    this.loaded = true;
    this.onLoaded?.();
  }

  initializeTerrain() {
    const size = this.size.width * this.size.height * this.size.width;
    this.idMap = new Uint8Array(size);
    this.instanceIdMap = new Int32Array(size).fill(-1);
  }

  _getIndex(x, y, z) {
    return (x * this.size.height * this.size.width) + (y * this.size.width) + z;
  }

  generateTerrain(rng) {
    const simplex = new SimplexNoise(rng);
    for (let x = 0; x < this.size.width; x++) {
      for (let z = 0; z < this.size.width; z++) {
        const biome = this.getBiome(simplex, x, z);
        const value = simplex.noise(
          (this.position.x + x) / this.params.terrain.scale,
          (this.position.z + z) / this.params.terrain.scale
        );
        const scaledNoise = this.params.terrain.offset + this.params.terrain.magnitude * value;
        let height = Math.floor(scaledNoise);
        height = Math.max(0, Math.min(height, this.size.height - 1));

        for (let y = this.size.height; y >= 0; y--) {
          if (y <= this.params.terrain.waterOffset && y === height) {
            this.setBlockId(x, y, z, blocks.sand.id);
          } else if (y === height) {
            let groundBlockType = this.getGroundBlock(biome);
            this.setBlockId(x, y, z, groundBlockType);
          } else if (y < height && this.getBlock(x, y, z).id === blocks.empty.id) {
            this.generateResourceIfNeeded(simplex, x, y, z);
          }
        }
      }
    }
  }

  getBiome(simplex, x, z) {
    let noise = 0.5 * simplex.noise(
      (this.position.x + x) / this.params.biomes.scale,
      (this.position.z + z) / this.params.biomes.scale
    ) + 0.5;

    noise += this.params.biomes.variation.amplitude * (simplex.noise(
      (this.position.x + x) / this.params.biomes.variation.scale,
      (this.position.z + z) / this.params.biomes.variation.scale
    ));

    if (noise < this.params.biomes.tundraToTemperate) return 'Tundra';
    if (noise < this.params.biomes.temperateToJungle) return 'Temperate';
    if (noise < this.params.biomes.jungleToDesert) return 'Jungle';
    return 'Desert';
  }

  getGroundBlock(biome) {
    return biome === 'Desert' ? blocks.sand.id :
      biome === 'Temperate' || biome === 'Jungle' ? blocks.grass.id :
        biome === 'Tundra' ? blocks.snow.id : blocks.grass.id;
  }

  generateFlyingIslands(rng) {
    const simplex = new SimplexNoise(rng);
    const config = {
      count: 1,
      minRadius: 8,
      maxRadius: 15,
      minHeight: Math.floor(this.size.height * 0.95),
      maxHeight: this.size.height - 3,
      baseThickness: 4,
      surfaceVariation: 3,
      cornerSmoothness: 0.9,
    };

    for (let i = 0; i < config.count; i++) {
      const centerX = Math.floor(rng.random() * this.size.width);
      const centerZ = Math.floor(rng.random() * this.size.width);
      const centerY = Math.floor(config.minHeight + rng.random() * (config.maxHeight - config.minHeight));
      const radius = Math.floor(config.minRadius + rng.random() * (config.maxRadius - config.minRadius));
      this.generateIsland(simplex, rng, centerX, centerY, centerZ, radius, config);
    }
  }

  generateIsland(simplex, rng, centerX, centerY, centerZ, radius, params) {
    const islandBiome = ['Temperate', 'Jungle', 'Desert', 'Tundra'][Math.floor(rng.random() * 4)];
    const ellipsoidFactor = 0.8;

    for (let x = centerX - radius; x <= centerX + radius; x++) {
      for (let z = centerZ - radius; z <= centerZ + radius; z++) {
        if (!this.inBounds(x, 0, z)) continue;
        const dx = (x - centerX) / radius;
        const dz = (z - centerZ) / radius;
        const distSquared = dx * dx + dz * dz;
        if (distSquared > 1) continue;

        const normalizedDist = Math.sqrt(distSquared);
        if (normalizedDist > params.cornerSmoothness && rng.random() > (1 - normalizedDist)) continue;

        const heightVariation = Math.floor(params.surfaceVariation * simplex.noise(
          (this.position.x + x) / (radius / 2),
          (this.position.z + z) / (radius / 2)
        ));
        const surfaceY = centerY + heightVariation;
        const thickness = Math.floor(params.baseThickness * (1 - ellipsoidFactor * normalizedDist));

        if (this.inBounds(x, surfaceY, z)) {
          this.setBlockId(x, surfaceY, z, this.getGroundBlock(islandBiome));
        }
        for (let y = 1; y <= thickness; y++) {
          if (this.inBounds(x, surfaceY - y, z)) {
            this.setBlockId(x, surfaceY - y, z, y <= 2 ? blocks.lava.id : blocks.stone.id);
          }
        }
      }
    }
  }

  generateClouds(rng) {
    const simplex = new SimplexNoise(rng);
    const cloudHeight = Math.floor(this.size.height * 0.98);

    for (let x = 0; x < this.size.width; x++) {
      for (let z = 0; z < this.size.width; z++) {
        const value = (simplex.noise(
          (this.position.x + x) / this.params.clouds.scale,
          (this.position.z + z) / this.params.clouds.scale
        ) + 1) * 0.5;
        if (value < this.params.clouds.density) {
          this.setBlockId(x, cloudHeight, z, blocks.cloud.id);
        }
      }
    }
  }


  generateResourceIfNeeded(simplex, x, y, z) {
    this.setBlockId(x, y, z, blocks.dirt.id);
    resources.forEach(resource => {
      const value = simplex.noise3d(
        (this.position.x + x) / resource.scale.x,
        (this.position.y + y) / resource.scale.y,
        (this.position.z + z) / resource.scale.z
      );
      if (value > resource.scarcity) {
        this.setBlockId(x, y, z, resource.id);
      }
    });
  }

  async loadPlayerChanges() {
    if (this.disposed) return;
    try {
      const chunkKey = `${this.position.x}_${this.position.z}`;
      console.log("Trying to load mods for chunk: ", chunkKey)
      const ref = doc(db, 'chunkMods', chunkKey);
      const snapshot = await getDoc(ref);

      if (snapshot.exists()) {
        const { blocks } = snapshot.data();
        if (blocks && typeof blocks === 'object') {
          for (const coord in blocks) {
            const [x, y, z] = coord.split('_').map(Number);
            if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
              this.setBlockId(x, y, z, blocks[coord]);
            } else {
              console.warn(`[WorldChunk] Invalid coordinate in Firestore: ${coord}`);
            }
          }
        } else {
          console.warn(`[WorldChunk] No valid block data for chunk ${chunkKey}`);
        }
      }
    } catch (error) {
      console.error(`[WorldChunk] Failed to load player changes for chunk ${this.position.x}_${this.position.z}:`, error);
    }
  }

  generateMeshes() {
    this.clear();
    this.generateWater();

    const counts = {};
    for (let x = 0; x < this.size.width; x++) {
      for (let y = 0; y < this.size.height; y++) {
        for (let z = 0; z < this.size.width; z++) {
          const block = this.getBlock(x, y, z);
          if (block.id !== blocks.empty.id && !this.isBlockObscured(x, y, z)) {
            counts[block.id] = (counts[block.id] || 0) + 1;
          }
        }
      }
    }

    const meshes = {};
    Object.keys(counts).forEach(blockId => {
      const id = Number(blockId);
      const blockType = Object.values(blocks).find(b => b.id === id);
      if (blockType) {
        const mesh = new THREE.InstancedMesh(geometry, blockType.material, counts[id]);
        mesh.name = id;
        mesh.count = 0;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.frustumCulled = false; // Fix for disappearing chunks
        meshes[id] = mesh;
      }
    });

    const matrix = new THREE.Matrix4();
    for (let x = 0; x < this.size.width; x++) {
      for (let y = 0; y < this.size.height; y++) {
        for (let z = 0; z < this.size.width; z++) {
          const blockId = this.getBlock(x, y, z).id;
          if (blockId === blocks.empty.id) continue;

          if (meshes[blockId] && !this.isBlockObscured(x, y, z)) {
            const mesh = meshes[blockId];
            if (mesh.count < mesh.instanceMatrix.count) {
              matrix.setPosition(x, y, z);
              mesh.setMatrixAt(mesh.count, matrix);
              this.setBlockInstanceId(x, y, z, mesh.count);
              mesh.count++;
            }
          }
        }
      }
    }

    this.add(...Object.values(meshes));
  }

  generateWater() {
    const material = new THREE.MeshLambertMaterial({ color: 0x9090e0, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthWrite: false });
    const waterMesh = new THREE.Mesh(new THREE.PlaneGeometry(), material);
    waterMesh.rotateX(-Math.PI / 2.0);
    waterMesh.position.set(this.size.width / 2, this.params.terrain.waterOffset + 0.4, this.size.width / 2);
    waterMesh.scale.set(this.size.width, this.size.width, 1);
    waterMesh.layers.set(1);
    this.add(waterMesh);
  }

  inBounds(x, y, z) {
    return x >= 0 && x < this.size.width && y >= 0 && y < this.size.height && z >= 0 && z < this.size.width;
  }

  getBlock(x, y, z) {
    if (!this.inBounds(x, y, z)) {
      return { id: blocks.empty.id, instanceId: null };
    }
    const idx = this._getIndex(x, y, z);
    const instanceId = this.instanceIdMap[idx];
    return {
      id: this.idMap[idx],
      instanceId: instanceId === -1 ? null : instanceId
    };
  }

  setBlockId(x, y, z, id) {
    if (this.inBounds(x, y, z)) {
      this.idMap[this._getIndex(x, y, z)] = id;
    }
  }

  setBlockInstanceId(x, y, z, instanceId) {
    if (this.inBounds(x, y, z)) {
      this.instanceIdMap[this._getIndex(x, y, z)] = instanceId === null ? -1 : instanceId;
    }
  }

  isBlockObscured(x, y, z) {
    const surrounding = [
      this.getBlock(x, y + 1, z), this.getBlock(x, y - 1, z),
      this.getBlock(x + 1, y, z), this.getBlock(x - 1, y, z),
      this.getBlock(x, y, z + 1), this.getBlock(x, y, z - 1)
    ];
    return surrounding.every(b => b?.id !== blocks.empty.id);
  }

  disposeInstances() {
    this.disposed = true;
    this.traverse((obj) => { if (obj.dispose) obj.dispose(); });
    this.clear();
  }

  /**
   * Adds a new block at (x,y,z) of type `blockId`
   * @param {number} x 
   * @param {number} y 
   * @param {number} z 
   * @param {number} blockId 
   */
  addBlock(x, y, z, blockId) {
    if (this.getBlock(x, y, z).id === blocks.empty.id) {
      this.setBlockId(x, y, z, blockId);
      this.addBlockInstance(x, y, z);
      this.dataStore.set(this.position.x, this.position.z, x, y, z, blockId);
    }
  }

  /**
   * Removes the block at (x, y, z)
   * @param {number} x 
   * @param {number} y 
   * @param {number} z 
   */
  removeBlock(x, y, z) {
    const block = this.getBlock(x, y, z);
    if (block && block.id !== blocks.empty.id) {
      this.deleteBlockInstance(x, y, z);
      this.setBlockId(x, y, z, blocks.empty.id);
      this.dataStore.set(this.position.x, this.position.z, x, y, z, blocks.empty.id);
    }
  }

  /**
   * Removes the mesh instance associated with `block` by swapping it
   * with the last instance and decrementing the instance count.
   * @param {number} x 
   * @param {number} y 
   * @param {number} z 
   */
  deleteBlockInstance(x, y, z) {
    const block = this.getBlock(x, y, z);
    console.log("DeleteBlockInstance: ", block)

    if (block.id === blocks.empty.id || block.instanceId === null) return;

    // Get the mesh and instance id of the block
    const mesh = this.children.find((instanceMesh) => instanceMesh.name == block.id);
    if (!mesh) {
      this.setBlockInstanceId(x, y, z, null);
      return;
    }
    const instanceId = block.instanceId;

    // Swapping the transformation matrix of the block in the last position
    // with the block that we are going to remove
    const lastMatrix = new THREE.Matrix4();
    mesh.getMatrixAt(mesh.count - 1, lastMatrix);

    // Updating the instance id of the block in the last position to its new instance id
    const v = new THREE.Vector3();
    v.applyMatrix4(lastMatrix);
    this.setBlockInstanceId(v.x, v.y, v.z, instanceId);

    // Swapping the transformation matrices
    mesh.setMatrixAt(instanceId, lastMatrix);

    // This effectively removes the last instance from the scene
    mesh.count--;

    // Notify the instanced mesh we updated the instance matrix
    // Also re-compute the bounding sphere so raycasting works
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();

    // Remove the instance associated with the block and update the data model
    this.setBlockInstanceId(x, y, z, null);
  }

  /**
   * Create a new instance for the block at (x,y,z)
   * @param {number} x 
   * @param {number} y 
   * @param {number} z
   */
  addBlockInstance(x, y, z) {
    const block = this.getBlock(x, y, z);
    if (!block || block.id === blocks.empty.id || block.instanceId !== null) return;

    let mesh = this.children.find((instanceMesh) => instanceMesh.name == block.id);
    const blockType = Object.values(blocks).find(b => b.id === block.id);

    // Create mesh if it doesn't exist
    if (!mesh) {
      if (!blockType) return;
      mesh = new THREE.InstancedMesh(geometry, blockType.material, 64); // Start with small capacity
      mesh.name = block.id;
      mesh.count = 0;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false; // Fix for disappearing chunks
      this.add(mesh);
    }

    // Resize mesh if full
    if (mesh.count >= mesh.instanceMatrix.count) {
      const newCapacity = mesh.instanceMatrix.count * 2;
      const newMesh = new THREE.InstancedMesh(geometry, blockType.material, newCapacity);
      newMesh.name = block.id;
      newMesh.count = mesh.count;
      newMesh.castShadow = true;
      newMesh.receiveShadow = true;
      newMesh.frustumCulled = false; // Fix for disappearing chunks

      // Copy existing matrices
      for (let i = 0; i < mesh.count; i++) {
        const matrix = new THREE.Matrix4();
        mesh.getMatrixAt(i, matrix);
        newMesh.setMatrixAt(i, matrix);
      }

      this.remove(mesh);
      mesh.dispose(); // Cleanup old mesh
      this.add(newMesh);
      mesh = newMesh;
    }

    const matrix = new THREE.Matrix4();
    matrix.setPosition(x, y, z);
    mesh.setMatrixAt(mesh.count, matrix);
    this.setBlockInstanceId(x, y, z, mesh.count);
    mesh.count++;
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
  }


}
